import React, { useState } from 'react';
import { FootHealthAssessment } from '../FootHealthMonitoring';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertCircle } from 'lucide-react';

interface FootConditionAnalyticsProps {
  assessment: FootHealthAssessment;
  baselineAssessment?: FootHealthAssessment;
  allAssessments: FootHealthAssessment[];
}

const FootConditionAnalytics: React.FC<FootConditionAnalyticsProps> = ({
  assessment,
  baselineAssessment,
  allAssessments
}) => {
  const [selectedCondition, setSelectedCondition] = useState<'plantarFasciitis' | 'diabeticNeuropathy' | 'footwear'>('plantarFasciitis');
  const [selectedFoot, setSelectedFoot] = useState<'left' | 'right' | 'both'>('right'); // Start with right foot as it shows the problem in our mock data
  
  // Helper function to format assessment dates for display
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Sort assessments by date (oldest to newest)
  const sortedAssessments = [...allAssessments].sort((a, b) => a.date.getTime() - b.date.getTime());
  
  // Prepare trend data
  const trendData = sortedAssessments.map(a => {
    let currentData: any = {
      date: formatDate(a.date),
      timestamp: a.date.getTime(),
      plantarFasciitisRisk: a.overallAssessment.plantarFasciitisRisk,
      diabeticNeuropathyIndicators: a.overallAssessment.diabeticNeuropathyIndicators,
      improperFootwearSigns: a.overallAssessment.improperFootwearSigns,
    };
    
    // Add foot-specific metrics
    if (selectedFoot === 'left' || selectedFoot === 'both') {
      currentData.leftHeelPressure = a.leftFootData.regions.heel.averagePressure;
      currentData.leftArchPressure = a.leftFootData.regions.medial_midfoot.averagePressure;
      currentData.leftSymmetry = a.leftFootData.aggregateMetrics.symmetryIndex;
    }
    
    if (selectedFoot === 'right' || selectedFoot === 'both') {
      currentData.rightHeelPressure = a.rightFootData.regions.heel.averagePressure;
      currentData.rightArchPressure = a.rightFootData.regions.medial_midfoot.averagePressure;
      currentData.rightSymmetry = a.rightFootData.aggregateMetrics.symmetryIndex;
    }
    
    return currentData;
  });
  
  // Helper to get current condition data for display
  const getCurrentConditionData = () => {
    switch (selectedCondition) {
      case 'plantarFasciitis':
        return {
          title: 'Plantar Fasciitis Risk Analysis',
          description: 'Tracking heel pressure and arch support indicators',
          risk: assessment.overallAssessment.plantarFasciitisRisk,
          baselineRisk: baselineAssessment?.overallAssessment.plantarFasciitisRisk,
          keyMetrics: [
            {
              name: 'Heel Pressure',
              current: selectedFoot === 'left' ? 
                assessment.leftFootData.regions.heel.averagePressure : 
                assessment.rightFootData.regions.heel.averagePressure,
              baseline: baselineAssessment ? (selectedFoot === 'left' ? 
                baselineAssessment.leftFootData.regions.heel.averagePressure : 
                baselineAssessment.rightFootData.regions.heel.averagePressure) : undefined,
              description: 'Elevated heel pressure is a key indicator of plantar fasciitis',
              isAbnormal: selectedFoot === 'right' && assessment.rightFootData.regions.heel.averagePressure > 60
            },
            {
              name: 'Arch Support',
              current: selectedFoot === 'left' ? 
                assessment.leftFootData.regions.medial_midfoot.averagePressure : 
                assessment.rightFootData.regions.medial_midfoot.averagePressure,
              baseline: baselineAssessment ? (selectedFoot === 'left' ? 
                baselineAssessment.leftFootData.regions.medial_midfoot.averagePressure : 
                baselineAssessment.rightFootData.regions.medial_midfoot.averagePressure) : undefined,
              description: 'Decreased arch pressure may indicate collapsing arch',
              isAbnormal: selectedFoot === 'right' && assessment.rightFootData.regions.medial_midfoot.averagePressure < 15
            },
            {
              name: 'Heel-Toe Pressure Ratio',
              current: selectedFoot === 'left' ? 
                assessment.leftFootData.regions.heel.averagePressure / assessment.leftFootData.regions.hallux.averagePressure : 
                assessment.rightFootData.regions.heel.averagePressure / assessment.rightFootData.regions.hallux.averagePressure,
              baseline: baselineAssessment ? (selectedFoot === 'left' ? 
                baselineAssessment.leftFootData.regions.heel.averagePressure / baselineAssessment.leftFootData.regions.hallux.averagePressure : 
                baselineAssessment.rightFootData.regions.heel.averagePressure / baselineAssessment.rightFootData.regions.hallux.averagePressure) : undefined,
              description: 'Indicates weight distribution from heel to toe',
              isAbnormal: selectedFoot === 'right' && 
                (assessment.rightFootData.regions.heel.averagePressure / assessment.rightFootData.regions.hallux.averagePressure) > 1.5
            }
          ],
          recommendations: assessment.overallAssessment.plantarFasciitisRisk > 50 ? [
            'Consider supportive footwear with arch support',
            'Perform daily plantar fascia stretches',
            'Apply ice to heel after prolonged standing',
            'Consult with a podiatrist for professional evaluation'
          ] : assessment.overallAssessment.plantarFasciitisRisk > 30 ? [
            'Wear supportive shoes with cushioned heels',
            'Monitor for morning heel pain',
            'Consider over-the-counter arch supports'
          ] : [
            'Maintain current foot health with proper footwear',
            'Continue regular foot stretching and exercises'
          ]
        };
      case 'diabeticNeuropathy':
        return {
          title: 'Diabetic Neuropathy Indicators',
          description: 'Tracking pressure distribution and potential hot spots',
          risk: assessment.overallAssessment.diabeticNeuropathyIndicators,
          baselineRisk: baselineAssessment?.overallAssessment.diabeticNeuropathyIndicators,
          keyMetrics: [
            {
              name: 'Pressure Distribution Evenness',
              current: selectedFoot === 'left' ? 
                assessment.leftFootData.aggregateMetrics.symmetryIndex : 
                assessment.rightFootData.aggregateMetrics.symmetryIndex,
              baseline: baselineAssessment ? (selectedFoot === 'left' ? 
                baselineAssessment.leftFootData.aggregateMetrics.symmetryIndex : 
                baselineAssessment.rightFootData.aggregateMetrics.symmetryIndex) : undefined,
              description: 'Uneven pressure may indicate nerve damage',
              isAbnormal: false
            },
            {
              name: 'High Pressure Points',
              current: Object.values(selectedFoot === 'left' ? 
                assessment.leftFootData.regions : 
                assessment.rightFootData.regions).reduce((max, region) => 
                  Math.max(max, region.peakPressure), 0),
              baseline: baselineAssessment ? 
                Object.values(selectedFoot === 'left' ? 
                  baselineAssessment.leftFootData.regions : 
                  baselineAssessment.rightFootData.regions).reduce((max, region) => 
                    Math.max(max, region.peakPressure), 0) : undefined,
              description: 'Elevated peak pressures increase ulcer risk',
              isAbnormal: false
            }
          ],
          recommendations: [
            'Inspect feet daily for injuries or pressure spots',
            'Wear properly fitted shoes with adequate toe box width',
            'Consider custom orthotics to distribute pressure evenly',
            'Maintain regular check-ups with healthcare provider'
          ]
        };
      case 'footwear':
        return {
          title: 'Footwear Fit Analysis',
          description: 'Assessing how current footwear affects foot pressure',
          risk: assessment.overallAssessment.improperFootwearSigns,
          baselineRisk: baselineAssessment?.overallAssessment.improperFootwearSigns,
          keyMetrics: [
            {
              name: 'Lateral Pressure',
              current: selectedFoot === 'left' ? 
                assessment.leftFootData.regions.lateral_forefoot.averagePressure : 
                assessment.rightFootData.regions.lateral_forefoot.averagePressure,
              baseline: baselineAssessment ? (selectedFoot === 'left' ? 
                baselineAssessment.leftFootData.regions.lateral_forefoot.averagePressure : 
                baselineAssessment.rightFootData.regions.lateral_forefoot.averagePressure) : undefined,
              description: 'High lateral pressure may indicate narrow shoes',
              isAbnormal: false
            },
            {
              name: 'Toe Pressure',
              current: selectedFoot === 'left' ? 
                assessment.leftFootData.regions.hallux.averagePressure : 
                assessment.rightFootData.regions.hallux.averagePressure,
              baseline: baselineAssessment ? (selectedFoot === 'left' ? 
                baselineAssessment.leftFootData.regions.hallux.averagePressure : 
                baselineAssessment.rightFootData.regions.hallux.averagePressure) : undefined,
              description: 'Elevated toe pressure may indicate improper shoe length',
              isAbnormal: false
            }
          ],
          recommendations: [
            'Ensure adequate toe box width in footwear',
            'Choose shoes with proper arch support',
            'Consider removable insoles for better pressure distribution',
            'Replace worn footwear regularly'
          ]
        };
      default:
        return {
          title: 'Foot Condition Analysis',
          description: '',
          risk: 0,
          keyMetrics: [],
          recommendations: []
        };
    }
  };
  
  const conditionData = getCurrentConditionData();
  
  // Determine which metrics to show in the trend chart
  const getChartConfig = () => {
    switch(selectedCondition) {
      case 'plantarFasciitis':
        if (selectedFoot === 'both') {
          return {
            lines: [
              { dataKey: 'leftHeelPressure', name: 'Left Heel Pressure', stroke: '#8884d8', yAxisId: 'left' },
              { dataKey: 'rightHeelPressure', name: 'Right Heel Pressure', stroke: '#82ca9d', yAxisId: 'left' },
              { dataKey: 'leftArchPressure', name: 'Left Arch Pressure', stroke: '#ffc658', strokeDasharray: '3 3', yAxisId: 'left' },
              { dataKey: 'rightArchPressure', name: 'Right Arch Pressure', stroke: '#ff8042', strokeDasharray: '3 3', yAxisId: 'left' }
            ]
          };
        } else if (selectedFoot === 'left') {
          return {
            lines: [
              { dataKey: 'leftHeelPressure', name: 'Heel Pressure', stroke: '#8884d8', yAxisId: 'left' },
              { dataKey: 'leftArchPressure', name: 'Arch Pressure', stroke: '#82ca9d', strokeDasharray: '3 3', yAxisId: 'left' },
              { dataKey: 'plantarFasciitisRisk', name: 'PF Risk Score', stroke: '#ff8042', yAxisId: 'risk' }
            ]
          };
        } else {
          return {
            lines: [
              { dataKey: 'rightHeelPressure', name: 'Heel Pressure', stroke: '#8884d8', yAxisId: 'left' },
              { dataKey: 'rightArchPressure', name: 'Arch Pressure', stroke: '#82ca9d', strokeDasharray: '3 3', yAxisId: 'left' },
              { dataKey: 'plantarFasciitisRisk', name: 'PF Risk Score', stroke: '#ff8042', yAxisId: 'risk' }
            ]
          };
        }
      case 'diabeticNeuropathy':
        if (selectedFoot === 'both') {
          return {
            lines: [
              { dataKey: 'leftSymmetry', name: 'Left Foot Symmetry', stroke: '#8884d8', yAxisId: 'left' },
              { dataKey: 'rightSymmetry', name: 'Right Foot Symmetry', stroke: '#82ca9d', yAxisId: 'left' },
              { dataKey: 'diabeticNeuropathyIndicators', name: 'Neuropathy Risk', stroke: '#ff8042', yAxisId: 'risk' }
            ]
          };
        } else {
          return {
            lines: [
              { dataKey: selectedFoot === 'left' ? 'leftSymmetry' : 'rightSymmetry', name: 'Foot Symmetry', stroke: '#8884d8', yAxisId: 'left' },
              { dataKey: 'diabeticNeuropathyIndicators', name: 'Neuropathy Risk', stroke: '#ff8042', yAxisId: 'risk' }
            ]
          };
        }
      case 'footwear':
        return {
          lines: [
            { dataKey: 'improperFootwearSigns', name: 'Improper Footwear', stroke: '#8884d8', yAxisId: 'left' }
          ]
        };
      default:
        return { lines: [] };
    }
  };
  
  const chartConfig = getChartConfig();
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">{conditionData.title}</h3>
        <div className="flex space-x-3">
          <div className="flex items-center space-x-1 text-sm">
            <button
              onClick={() => setSelectedFoot('left')}
              className={`px-3 py-1 rounded-l-md ${
                selectedFoot === 'left' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Left
            </button>
            <button
              onClick={() => setSelectedFoot('both')}
              className={`px-3 py-1 ${
                selectedFoot === 'both' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Both
            </button>
            <button
              onClick={() => setSelectedFoot('right')}
              className={`px-3 py-1 rounded-r-md ${
                selectedFoot === 'right' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Right
            </button>
          </div>
          
          <div className="flex items-center space-x-1 text-sm">
            <button
              onClick={() => setSelectedCondition('plantarFasciitis')}
              className={`px-3 py-1 rounded-l-md ${
                selectedCondition === 'plantarFasciitis' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Plantar Fasciitis
            </button>
            <button
              onClick={() => setSelectedCondition('diabeticNeuropathy')}
              className={`px-3 py-1 ${
                selectedCondition === 'diabeticNeuropathy' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Neuropathy
            </button>
            <button
              onClick={() => setSelectedCondition('footwear')}
              className={`px-3 py-1 rounded-r-md ${
                selectedCondition === 'footwear' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Footwear
            </button>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Risk indicator */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <AlertCircle className={`mr-2 ${
              conditionData.risk > 60 ? 'text-red-500' : 
              conditionData.risk > 30 ? 'text-yellow-500' : 
              'text-green-500'
            }`} size={20} />
            <h4 className="font-medium">Risk Assessment</h4>
          </div>
          
          <div className={`text-3xl font-bold mb-2 ${
            conditionData.risk > 60 ? 'text-red-600' : 
            conditionData.risk > 30 ? 'text-yellow-600' : 
            'text-green-600'
          }`}>
            {conditionData.risk}%
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
            <div 
              className={`h-2.5 rounded-full ${
                conditionData.risk > 60 ? 'bg-red-500' : 
                conditionData.risk > 30 ? 'bg-yellow-500' : 
                'bg-green-500'
              }`}
              style={{ width: `${conditionData.risk}%` }}
            ></div>
          </div>
          
          <div className="text-sm text-gray-600">
            {conditionData.risk > 60 ? (
              <span className="text-red-600 font-medium">High risk. Medical consultation recommended.</span>
            ) : conditionData.risk > 30 ? (
              <span className="text-yellow-600 font-medium">Moderate risk. Preventative measures advised.</span>
            ) : (
              <span className="text-green-600 font-medium">Low risk. Continue monitoring.</span>
            )}
          </div>
          
          {baselineAssessment && (
            <div className="mt-3 text-sm">
              <span className="font-medium">Change from baseline: </span>
              <span className={
                conditionData.risk - conditionData.baselineRisk! > 20 ? 'text-red-600 font-medium' :
                conditionData.risk - conditionData.baselineRisk! > 10 ? 'text-yellow-600 font-medium' :
                'text-gray-600'
              }>
                {conditionData.risk > conditionData.baselineRisk! ? '+' : ''}
                {(conditionData.risk - conditionData.baselineRisk!).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
        
        {/* Key indicators */}
        <div className="md:col-span-2 bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-3">Key Indicators</h4>
          
          <div className="space-y-4">
            {conditionData.keyMetrics.map((metric, index) => (
              <div key={index} className={`p-3 rounded-lg border ${
                metric.isAbnormal ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'
              }`}>
                <div className="flex justify-between">
                  <span className="font-medium">{metric.name}</span>
                  <div className="flex items-center">
                    <span className={metric.isAbnormal ? 'text-red-600 font-medium' : ''}>
                      {typeof metric.current === 'number' ? metric.current.toFixed(1) : metric.current}
                    </span>
                    
                    {metric.baseline !== undefined && (
                      <span className="text-gray-500 text-sm ml-2">
                        (Baseline: {typeof metric.baseline === 'number' ? metric.baseline.toFixed(1) : metric.baseline})
                      </span>
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mt-1">{metric.description}</p>
                
                {metric.baseline !== undefined && typeof metric.current === 'number' && typeof metric.baseline === 'number' && (
                  <div className="mt-2 text-xs">
                    <span className={`font-medium ${
                      Math.abs(((metric.current - metric.baseline) / metric.baseline) * 100) > 20 
                        ? metric.current > metric.baseline ? 'text-red-600' : 'text-blue-600'
                        : 'text-gray-600'
                    }`}>
                      {metric.current > metric.baseline ? '+' : ''}
                      {(((metric.current - metric.baseline) / metric.baseline) * 100).toFixed(1)}% from baseline
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Trends chart */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="font-medium mb-4">Trend Analysis</h4>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              {selectedCondition !== 'footwear' && (
                <YAxis yAxisId="risk" orientation="right" domain={[0, 100]} />
              )}
              <Tooltip />
              <Legend />
              
              {chartConfig.lines.map((line, index) => (
                <Line
                  key={index}
                  type="monotone"
                  dataKey={line.dataKey}
                  name={line.name}
                  stroke={line.stroke}
                  strokeDasharray={line.strokeDasharray}
                  yAxisId={line.yAxisId}
                  activeDot={{ r: 8 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Recommendations */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium mb-3">Recommendations</h4>
        
        <ul className="space-y-2">
          {conditionData.recommendations.map((rec, index) => (
            <li key={index} className="flex items-start">
              <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-2 mt-0.5">
                {index + 1}
              </div>
              <span className="text-gray-700">{rec}</span>
            </li>
          ))}
        </ul>
        
        {selectedCondition === 'plantarFasciitis' && conditionData.risk > 50 && (
          <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 flex items-start">
            <AlertCircle size={16} className="mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-sm">
              Your data shows significant indicators of developing plantar fasciitis. 
              A professional medical evaluation is recommended.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FootConditionAnalytics; 